import React, { useContext, useMemo } from 'react';
import { LangContext } from '../../contexts/langContext';
import { StateContext } from '../../contexts/stateContext';
import { astroSvgs } from '../../static/astro';
import { getVarnaFromChartData, getGanaFromChartData, getNithyaYogaFromChartData } from '../../static/jyotish_utils';
import { yoni_dictionary, nakshatra_yoni_dictionary, nithya_yoga_dictionary } from '../../static/jyotish_local';
import jyotish_placements from '../../static/jyotish_placements.json';
import { planets_dictionary } from '../../static/planets_dictionary';
import { AstroBg, AstroSign } from '../UiKit/AstroSign';
import { Button } from '../UiKit/Button';
import { DescriptionIco } from '../UiKit/DescriptionIco';
import { Svg } from '../UiKit/Svg';
import { PopoverH, TooltipH } from '../UiKit/TooltipH';
import { Txt } from '../UiKit/Txt';
import { TagCloud } from './Tags';
import { formatJyotishDegree } from './utilsJyotish';
import '../../components/css/astro.css';
import { DropDownDivider } from '../UiKit/DropDown';
import { Icon } from '../UiKit/Icon';

const NITHYA_NATURE_COLORS = {
  most_auspicious: '#3fb56b',
  favorable: '#6bbf59',
  less_favorable: '#d9a441',
  inauspicious: '#cf5b5b',
};

const getYoniByNakshatra = (nakshatraName) => {
  if (!nakshatraName || typeof nakshatraName !== 'string') return null;
  const normalizedName = nakshatraName.toLowerCase().replace(/\s+/g, '_');
  const yoniInfo = nakshatra_yoni_dictionary[normalizedName];
  if (!yoniInfo) return null;
  return {
    emoji: yoniInfo.yoniEmoji || '–',
    title: yoniInfo.yoniTitle || '–',
    id: yoniInfo.yoniId || null,
  };
};

const wrapItem = (itemClassName, key, children) => {
  if (!itemClassName) return children;
  return <div key={key} className={itemClassName}>{children}</div>;
};

export const JYOTISH_SUMMARY_SECTIONS = ['moon', 'lagna', 'sun', 'yoni', 'varna', 'gana', 'nithya'];

/** Summary: Moon/Lagna/Sun + Yoni/Varna/Gana/Nithya — shared by JyotishInfo and PentaJyotish */
export function JyotishSummary({ kundli, itemClassName, only }) {
  const context = { ...useContext(LangContext), ...useContext(StateContext) };
  const $ui = context.$ui;
  const $jyotish = context.$jyotish;
  const varnaBy = context.state?.varnaBy ?? 'nakshatra';

  const varnaData = useMemo(() => getVarnaFromChartData(kundli, varnaBy), [kundli, varnaBy]);
  const ganaData = useMemo(() => getGanaFromChartData(kundli), [kundli]);
  const nithyaYogaData = useMemo(() => getNithyaYogaFromChartData(kundli), [kundli]);

  if (!kundli) return null;

  const moonNakshatraName = kundli.dasha?.birthNakshatra || '–';
  const moonYoni = getYoniByNakshatra(moonNakshatraName);

  const withLang = (dict, str, field) => {
    const language = context.language;
    return dict[str] ? dict[str][`${field}-${language}`] : str;
  };

  const $placement = (point, sign) => jyotish_placements[point]?.[sign]?.[context.language] || jyotish_placements[point]?.[sign]?.en || '';
  const $placementTitle = (titleKey, sign) => sign ? `${$ui(titleKey)} ${$ui(`sign_in_${sign}`)}` : '';

  const moonCell = kundli.planets?.Moon?.rashiName && (
    <div className="astro-summary-cell-container">
      <div className="astro-summary-cell astro-summary-cell-jyotish _fv _fgap0">
        <AstroBg sign={kundli.planets.Moon.rashiName} />
        <div className="_fv _fm _tc _fgap0">
          <div className="astro-header">
            <TooltipH title={<div className="_fv _fgap02 ">
              <b>{$ui('planet_Moon')}</b>
              {moonNakshatraName && moonNakshatraName !== '–' && <Txt>{moonNakshatraName}</Txt>}
            </div>}>
              <span>
                <Svg color="#A8A8A8" width={20} height={20}>{planets_dictionary['Moon'].svg}</Svg>
              </span>
            </TooltipH>
          </div>
          <AstroSign sign={kundli.planets.Moon.rashiName} />
          <strong className="jyo_rashi_name">{$ui(kundli.planets.Moon.rashiName) || '–'}</strong>
          <span className="jyo_rashi_degree">{formatJyotishDegree(kundli.planets.Moon.degree)} {kundli.planets.Moon.isRetrograde ? ' R' : ''} {moonNakshatraName}</span>
        </div>
      </div>
      <div className="astro-summary-cell-description">
        <b>{$placementTitle('planet_Moon', kundli.planets.Moon.rashiName)}</b>
        <Txt>{$placement('Moon', kundli.planets.Moon.rashiName)}</Txt>
      </div>
    </div>
  );

  const lagnaCell = kundli.ascendant && (
    <div className="astro-summary-cell-container">
      <div className="astro-summary-cell astro-summary-cell-jyotish _fv _fgap0">
        <AstroBg sign={kundli.ascendant.rashiName} />
        <div className="_fv _fm _tc _fgap0">
          <div className="astro-header">
            <TooltipH title={<div className="_fv _fgap02 ">
              <b>{$ui('jyotish_lagna_label')}</b>
              <Txt>{$ui('jyotish_lagna_hint')}</Txt>
            </div>}>
              <span>
                <Svg color="#A8A8A8" width={20} height={20}>{astroSvgs['asc']}</Svg>
              </span>
            </TooltipH>
          </div>
          <AstroSign sign={kundli.ascendant.rashiName} />
          <strong className="jyo_rashi_name">{$ui(kundli.ascendant.rashiName) || '–'}</strong>
          <span className="jyo_rashi_degree">{formatJyotishDegree(kundli.ascendant.longitude != null ? (kundli.ascendant.longitude % 30) : null)}{kundli.ascendant.nakshatra ? ` · ${kundli.ascendant.nakshatra} (${kundli.ascendant.pada} pada)` : ''}</span>
        </div>
      </div>
      <div className="astro-summary-cell-description">
        <b>{$placementTitle('jyotish_lagna_label', kundli.ascendant.rashiName)}</b>
        <Txt>{$placement('Lagna', kundli.ascendant.rashiName)}</Txt>
      </div>
    </div>
  );

  const sunCell = kundli.planets?.Sun && (
    <div className="astro-summary-cell-container">
      <div className="astro-summary-cell astro-summary-cell-jyotish _fv _fgap0">
        <AstroBg sign={kundli.planets.Sun.rashiName} />
        <div className="_fv _fm _tc _fgap0">
          <div className="astro-header">
            <TooltipH title={<div className="_fv _fgap02 ">
              <b>{$ui('planet_Sun')}</b>
            </div>}>
              <span style={{ marginTop: '-2px', marginBottom: '-2px' }}>
                <Svg color="#A8A8A8" width={24} height={24}>{planets_dictionary['Sun'].svg}</Svg>
              </span>
            </TooltipH>
          </div>
          <AstroSign sign={kundli.planets.Sun.rashiName} />
          <strong className="jyo_rashi_name">{$ui(kundli.planets.Sun.rashiName) || '–'}</strong>
          <span className="jyo_rashi_degree">{formatJyotishDegree(kundli.planets.Sun.degree)}{kundli.planets.Sun.isRetrograde ? ' R' : ''}</span>
        </div>
      </div>
      <div className="astro-summary-cell-description">
        <b>{$placementTitle('planet_Sun', kundli.planets.Sun.rashiName)}</b>
        <Txt>{$placement('Sun', kundli.planets.Sun.rashiName)}</Txt>
      </div>
    </div>
  );

  const signsRow = (
    <div className="x_astrology_summary" style={{ position: 'relative', borderRadius: '0.4em' }}>
      {moonCell}
      {lagnaCell}
      {sunCell}
    </div>
  );

  const yoniBlock = moonYoni && (
    <div className="astro-summary-cell astro-cell-jyotish _fv _fgap02">
      <div className="_fv _fl _fgap02">
        <div className="astro-header astro-header-j">
          <div className="_f _fgap04">
            <strong className="">{$ui('jyotish_yoni_label_short')}</strong><DescriptionIco><Txt>{$ui('jyotish_yoni_description')}</Txt></DescriptionIco>
          </div>
        </div>
        <div className="x_edu_channel_h1 _f _fm _fgap06">
          <span className="t12">{moonYoni.emoji}</span> <strong>{withLang(yoni_dictionary, moonYoni.id, 'title')}</strong>
        </div>
        <div className="x_item_desc">
          <span className="x_edu_item_desc">{withLang(yoni_dictionary, moonYoni.id, 'description')}</span>
        </div>
      </div>
    </div>
  );

  const varnaBlock = varnaData.varna && (
    <div className="astro-summary-cell astro-cell-jyotish _fv _fgap02">
      <div className="_fv _fl _fgap02">
        <div className="astro-header astro-header-j">
          <div className="_fm _f100">
            <strong className=" _f100">{$ui('jyotish_varna_label')}</strong>
            <div className="x_jyotish_varna_select _f">
              <PopoverH
                placement="bottomRight"
                title={<div className="_fv _fgap02">
                  <Button
                    className="_f100"
                    ghost={varnaBy !== 'nakshatra'}
                    sm
                    onClick={() => context.setState.setVarnaBy('nakshatra')}
                  >
                    By Nakshatra
                  </Button>
                  <Button
                    className="_f100"
                    ghost={varnaBy !== 'moonSign'}
                    sm
                    onClick={() => context.setState.setVarnaBy('moonSign')}
                  >
                    By Moon Sign
                  </Button>
                </div>}
              >
                <Button ghost sm iconAfter="expand_more">
                  {varnaBy === 'moonSign' ? 'By Moon Sign' : 'By Nakshatra'}
                </Button>
              </PopoverH>
            </div>
          </div>
        </div>
        <div className="x_edu_channel_h1 "><span className="t12">{$jyotish(`j_${varnaData.varna.toLowerCase()}`, 'emoji')}</span> <strong>{$jyotish(`j_${varnaData.varna.toLowerCase()}`, 'desc1')}</strong></div>
        <div className="x_item_desc">
          <span className="x_edu_item_desc">{$jyotish(`j_${varnaData.varna.toLowerCase()}`, 'description')}</span>
        </div>
      </div>
    </div>
  );

  const ganaBlock = ganaData.gana && (
    <div className="astro-summary-cell astro-cell-jyotish _fv _fgap02">
      <div className="_fv _fl _fgap02">
        <div className="astro-header astro-header-j">
          <TooltipH title={<div className="_fv _fgap02 "><b>{$ui('jyotish_gana_label')}</b></div>}>
            <strong className="">{$ui('jyotish_gana_label')}</strong>
          </TooltipH>
        </div>
        <div className="_fv _fgap02">
          <div className="x_edu_channel_h1 _fh _fnowrap _fgap02"><span className="t12">{$jyotish(`j_${ganaData.gana.toLowerCase()}`, 'emoji')}</span><Txt>{$jyotish(`j_${ganaData.gana.toLowerCase()}`, 'desc1')}</Txt></div>
          <div className="x_item_desc">
            <span className="x_edu_item_desc">{$jyotish(`j_${ganaData.gana.toLowerCase()}`, 'description')}</span>
          </div>
          <div className="">
            <TagCloud label={<Icon>label</Icon>} flat={true} tagline={$jyotish(`j_${ganaData.gana.toLowerCase()}`, 'tags')} />
          </div>
        </div>
      </div>
    </div>
  );

  const nithyaBlock = nithyaYogaData.yoga && (
    <div className="astro-summary-cell astro-cell-jyotish _fv _fgap02">
      <div className="_fv _fl _fgap02">
        <div className="astro-header astro-header-j">
          {$ui('jyotish_nithya_yoga_label')} <DescriptionIco><h3>{$ui('jyotish_nithya_yoga_label')}</h3><Txt>{$ui('jyotish_nithya_yoga_hint')}</Txt></DescriptionIco>
        </div>

        <div className="_f _fgap06">
          <span className="t12">{nithyaYogaData.yoga.emoji}</span>
          <div className="x_edu_channel_h1">
            <strong>{withLang(nithya_yoga_dictionary, nithyaYogaData.id, 'title')}</strong>
          </div>
        </div>
          
          {nithyaYogaData.yoga.nature && (
            <span
              className="_t08"
              style={{
                borderRadius: '0.8em',
                color: '#fff',
                padding: '0em 0.6em',
                background: NITHYA_NATURE_COLORS[nithyaYogaData.yoga.nature] || '#888',
              }}
            >
              {$ui(`jyotish_nithya_nature_${nithyaYogaData.yoga.nature}`)}
            </span>
          )}
        <div className="x_item_desc">
          <span className="x_edu_item_desc">{withLang(nithya_yoga_dictionary, nithyaYogaData.id, 'description')}</span>
        </div>
        {withLang(nithya_yoga_dictionary, nithyaYogaData.id, 'goodfor') && (
          <span className=" _o06">
            <b>{$ui('jyotish_nithya_yoga_goodfor')}:</b> {withLang(nithya_yoga_dictionary, nithyaYogaData.id, 'goodfor')}
          </span>
        )}
        <div className="">
          <TagCloud label={<Icon>label</Icon>} flat={true} tagline={withLang(nithya_yoga_dictionary, nithyaYogaData.id, 'keyword')} />
        </div>  
        {typeof nithyaYogaData.progress === 'number' && (
          <TooltipH title={<div className="">{$ui('jyotish_nithya_yoga_progress')}: {Math.round(nithyaYogaData.progress * 100)}% {$ui('jyotish_nithya_yoga_label')} → {withLang(nithya_yoga_dictionary, nithyaYogaData.nextId, 'title')}</div>}>
            <div className="_fm _fgap06 _t06 _o06 _f100" style={{ marginTop: '0.3em' }}>
              <div style={{ flex: 1, height: '1px', borderRadius: '2px', background: 'rgba(128,128,128,0.25)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.round(nithyaYogaData.progress * 100)}%`, height: '100%', background: '#666' }} />
              </div>
              <span>{Math.round(nithyaYogaData.progress * 100)}%</span>
            </div>
          </TooltipH>
        )}
      </div>
    </div>
  );

  const sections = {
    moon: moonCell,
    lagna: lagnaCell,
    sun: sunCell,
    signs: signsRow,
    yoni: yoniBlock,
    varna: varnaBlock,
    gana: ganaBlock,
    nithya: nithyaBlock,
  };

  if (only) {
    const block = sections[only];
    if (!block) return null;
    return wrapItem(itemClassName, only, block);
  }

  if (itemClassName) {
    return (
      <>
        {wrapItem(itemClassName, 'signs', signsRow)}
        {yoniBlock && wrapItem(itemClassName, 'yoni', yoniBlock)}
        {varnaBlock && wrapItem(itemClassName, 'varna', varnaBlock)}
        {ganaBlock && wrapItem(itemClassName, 'gana', ganaBlock)}
        {nithyaBlock && wrapItem(itemClassName, 'nithya', nithyaBlock)}
      </>
    );
  }

  return (
    <>
      {signsRow}
      <DropDownDivider />
      <div className="x_jyotish_summary _fv _fgap1 _f100" style={{ position: 'relative', overflow: 'hidden', borderRadius: '0.4em' }}>
        {yoniBlock}
        <DropDownDivider />
        {varnaBlock}
        <DropDownDivider />
        {ganaBlock}
        <DropDownDivider />
        {nithyaBlock}
      </div>
    </>
  );
}
